import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Btn, Chip } from "@/components/kit";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Rocket, WarningCircle } from "@phosphor-icons/react";
import { useNavigate, Link } from "react-router-dom";

export default function UnresolvedDrafts() {
  const m = useMasters();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/unresolved_drafts");
      setRows(data.drafts);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const promote = async (id) => {
    if (!window.confirm("Promote to submitted? Stock will be deducted.")) return;
    try {
      await api.post(`/jobs/${id}/promote`);
      toast.success("Promoted — stock deducted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const promoteAll = async () => {
    const eligible = rows.filter(r => !r.insufficient_stock);
    if (eligible.length === 0) return toast.error("No drafts are ready to promote (all have insufficient stock).");
    if (!window.confirm(`Promote ${eligible.length} draft(s)? Each will deduct its plate quantity from stock.`)) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (const d of eligible) {
      try { await api.post(`/jobs/${d.id}/promote`); ok++; }
      catch { fail++; }
    }
    setBusy(false);
    toast.success(`Promoted ${ok}. ${fail>0?fail+' failed.':''}`);
    load();
  };

  const cancel = async (id) => {
    const reason = window.prompt("Cancellation reason?");
    if (!reason) return;
    try { await api.post(`/jobs/${id}/cancel`, { reason }); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const blocked = rows.filter(r => r.insufficient_stock).length;
  const ready = rows.length - blocked;

  return (
    <div data-testid="unresolved-page">
      <Toaster position="bottom-right" richColors />
      <PageHeader
        title="Unresolved Drafts"
        subtitle="Draft jobs that have not yet consumed stock. Promote when ready, or cancel to discard."
        actions={
          <div className="flex gap-2">
            <Btn variant="outline" onClick={()=>nav("/app")} data-testid="unresolved-back-btn"><ArrowLeft size={14} /> Dashboard</Btn>
            <Btn variant="primary" onClick={promoteAll} disabled={busy || ready===0} data-testid="unresolved-promote-all-btn">
              <Rocket size={14} /> Promote {ready} Ready
            </Btn>
          </div>
        }
      />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Card testid="unresolved-total-card" className="p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Total Drafts</div>
            <div className="font-heading font-black text-3xl mt-1">{rows.length}</div>
          </Card>
          <Card testid="unresolved-ready-card" className="p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Ready to Promote</div>
            <div className="font-heading font-black text-3xl mt-1 text-emerald-700">{ready}</div>
          </Card>
          <Card testid="unresolved-blocked-card" className="p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Blocked (Insufficient Stock)</div>
            <div className="font-heading font-black text-3xl mt-1 text-red-700">{blocked}</div>
          </Card>
        </div>

        <Card>
          <table className="w-full data-table" data-testid="unresolved-table">
            <thead><tr>
              <th className="px-3 py-2 text-left">Job No.</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Job / File</th>
              <th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-left">Workshop</th>
              <th className="px-3 py-2 text-left">Size</th>
              <th className="px-3 py-2 text-right">Need</th>
              <th className="px-3 py-2 text-right">Available</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-sm">No drafts. All jobs are submitted or cancelled.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} className={r.insufficient_stock ? "bg-red-50/40" : ""} data-testid={`unresolved-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{r.job_number}</td>
                  <td className="px-3 py-2 font-mono">{r.job_date}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{r.job_name}</div>
                    <div className="text-[11px] text-zinc-500 font-mono truncate max-w-xs">{r.file_name}</div>
                  </td>
                  <td className="px-3 py-2">{nameOf(m.clients, r.client_id)}</td>
                  <td className="px-3 py-2 font-mono">{nameOf(m.machines, r.machine_id)}</td>
                  <td className="px-3 py-2 font-mono">{m.plate_sizes.find(p=>p.id===r.plate_size_id)?.canonical || "-"}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">{r.final_plate_qty}</td>
                  <td className={`px-3 py-2 text-right font-mono ${r.insufficient_stock?"text-red-600 font-bold":""}`}>{r.available_stock}</td>
                  <td className="px-3 py-2">
                    {r.insufficient_stock
                      ? <Chip tone="bad">Short {r.shortfall}</Chip>
                      : <Chip tone="good">Ready</Chip>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Btn variant="ghost" onClick={()=>cancel(r.id)} data-testid={`unresolved-cancel-${r.id}`}>Cancel</Btn>
                    <Btn variant="primary" onClick={()=>promote(r.id)} disabled={r.insufficient_stock} data-testid={`unresolved-promote-${r.id}`}>Promote</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        {blocked > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 flex items-start gap-2 text-sm">
            <WarningCircle size={16} weight="fill" className="text-amber-600 mt-0.5" />
            <div>
              {blocked} draft{blocked===1?"":"s"} cannot be promoted right now because current stock is lower than the plate quantity they need. Enter more purchases, transfer stock in, or cancel the affected drafts.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
